import Box from '@material-ui/core/Box';
import { ThemeProvider } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useCachePaginatedQuery } from 'butler';
import MUIDataTable from 'mui-datatables';
import qs from 'qs';
import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useHistory } from 'react-router-dom';
import { Validation as yup } from '@local/website-stdlib';
import useCompaniesListApi from '../../api/useCompaniesListApi';
import ButtonLink from '../components/ButtonLink';
import InternalLink from '../components/InternalLink';
import { genLoadingTableFooter } from '../components/ProgressTableFooter';
import TableListTheme from '../components/TableListTheme';
const PARAMETERS_VALIDATION = yup.object().shape({
    page: yup.number().min(1).default(1),
    pageSize: yup.number().min(1).default(10),
    sort: yup.string().min(0).default('name'),
});
function toQueryParameters(parameters) {
    const queryParameters = {
        page: parameters.page + 1,
        pageSize: parameters.pageSize,
        sort: parameters.sort,
    };
    return queryParameters;
}
function fromQueryParameters(queryParameters) {
    let initState;
    try {
        initState = PARAMETERS_VALIDATION.validateSync(queryParameters);
        initState.page -= 1;
        initState.pageSize = Math.max(5, initState.pageSize);
    }
    catch (e) {
        initState = {
            page: 0,
            pageSize: 25,
        };
    }
    return initState;
}
function companiesSearchReducer(state, action) {
    switch (action.type) {
        case 'changePage':
            return { ...state, page: action.page };
        case 'changeRowsPerPage':
            return { ...state, page: 0, pageSize: action.rowsPerPage };
        case 'sort':
            let sort = action.sortOrder.name;
            if (action.sortOrder.direction === 'desc') {
                sort = `-${sort}`;
            }
            return { ...state, sort };
        default:
            return state;
    }
}
const CompaniesListComponent = () => {
    // Sync the parameters and our URL
    const history = useHistory();
    const initParameters = useMemo(() => {
        const queryParameters = qs.parse(history.location.search, {
            ignoreQueryPrefix: true,
        });
        return fromQueryParameters(queryParameters);
    }, [history]);
    const [parameters, dispatchParameters] = useReducer(companiesSearchReducer, initParameters);
    useEffect(() => {
        const queryParameters = toQueryParameters(parameters);
        history.replace(`${history.location.pathname}?${qs.stringify(queryParameters)}`);
    }, [history, parameters]);
    const companiesListApi = useCompaniesListApi();
    const companiesQuery = useCachePaginatedQuery(['companies', parameters], companiesListApi.handleRequest, companiesListApi.handleTransform);
    const handleTableChange = useCallback((action, tableState) => {
        if (action === 'changePage' ||
            action === 'changeRowsPerPage' ||
            action === 'sort') {
            dispatchParameters({ type: action, ...tableState });
        }
    }, [dispatchParameters]);
    const listInfo = {
        items: [],
        totalCount: 0,
    };
    if (companiesQuery.resolvedData) {
        listInfo.items = companiesQuery.resolvedData.items;
        listInfo.totalCount = companiesQuery.resolvedData.totalCount;
    }
    const CustomFooter = genLoadingTableFooter(companiesQuery.isFetching);
    return (React.createElement(Box, { width: "100%" },
        React.createElement(Box, { width: "100%", display: "flex", flexDirection: "row", justifyContent: "flex-end", marginBottom: 4 },
            React.createElement(ButtonLink, { to: "/company/create", variant: "contained", color: "primary", disableElevation: true }, "Create Company")),
        React.createElement(ThemeProvider, { theme: TableListTheme },
            React.createElement(MUIDataTable, { title: React.createElement(Box, { paddingY: 2 },
                    React.createElement(Typography, { variant: "h5" }, "Companies")), data: listInfo.items, columns: [
                    {
                        label: 'Name',
                        name: 'name',
                        options: {
                            searchable: true,
                            print: false,
                            sort: true,
                            draggable: false,
                            viewColumns: false,
                            customBodyRender: (name, tableMeta) => (React.createElement(InternalLink, { to: `/company/${tableMeta.rowData[2]}` }, name)),
                        },
                    },
                    {
                        label: 'Last updated',
                        name: 'updatedAt',
                        options: {
                            searchable: true,
                            print: false,
                            sort: true,
                            draggable: false,
                            viewColumns: false,
                            customBodyRender: (date) => date.toLocaleDateString(),
                        },
                    },
                    {
                        label: 'Forecast',
                        name: 'id',
                        options: {
                            searchable: true,
                            print: false,
                            sort: false,
                            draggable: false,
                            viewColumns: false,
                            customBodyRender: (companyId) => (React.createElement(ButtonLink, { to: `/company/${companyId}/forecast`, variant: "contained", color: "primary", disableElevation: true }, "View Forecasts")),
                        },
                    },
                    {
                        label: 'Files',
                        name: 'id',
                        options: {
                            searchable: true,
                            print: false,
                            sort: false,
                            draggable: false,
                            viewColumns: false,
                            customBodyRender: (companyId) => (React.createElement(ButtonLink, { to: `/company/${companyId}`, variant: "contained", color: "secondary", disableElevation: true }, "Upload Files")),
                        },
                    },
                ], options: {
                    serverSide: true,
                    elevation: 2,
                    search: true,
                    print: false,
                    download: false,
                    selectableRows: 'none',
                    viewColumns: false,
                    filter: false,
                    responsive: 'standard',
                    // List features
                    page: parameters.page,
                    rowsPerPage: parameters.pageSize,
                    rowsPerPageOptions: [5, 10, 25],
                    count: listInfo.totalCount,
                    onTableChange: handleTableChange,
                }, components: {
                    TableFooter: CustomFooter,
                } }))));
};
export default CompaniesListComponent;
